import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";

// Icons
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InputIcon from "@mui/icons-material/Input";
import OutputIcon from "@mui/icons-material/Output";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  getSubOrderProductById,
  toggleSubOrderProductStatus,
} from "../../../services/SubOrderProduct";
import { getImportsByCategory } from "../../../services/Import";
import CategoryItemsViewDialog from "../../../components/CategoryItemsViewDialog";

function SubOrderProductView() {
  const theme = useTheme();
  const history = useHistory();
  const { subProductId } = useParams();
  const [subOrderProduct, setSubOrderProduct] = useState(null);
  const [inputItems, setInputItems] = useState([]);
  const [outputItems, setOutputItems] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [categoryViewOpen, setCategoryViewOpen] = useState(false);
  const [categoryViewName, setCategoryViewName] = useState("");
  const [categoryViewItems, setCategoryViewItems] = useState([]);

  const handleViewCategoryItems = async (catId, catName) => {
    const items = await getImportsByCategory(catId);
    setCategoryViewName(catName);
    setCategoryViewItems(Array.isArray(items) ? items : []);
    setCategoryViewOpen(true);
  };

  const handleToggleStatus = async () => {
    setLoading(true);
    try {
      const updated = await toggleSubOrderProductStatus(subProductId);
      if (updated) {
        setSubOrderProduct(updated);
      }
    } catch (error) {
      console.error("Error toggling status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getSubOrderProductById(subProductId);

      if (result) {
        setSubOrderProduct(result);

        // Separate input and output items
        const inputs = result.subOrderItems.filter(
          (item) => item.itemType === "INPUT"
        );
        const outputs = result.subOrderItems.filter(
          (item) => item.itemType === "OUTPUT"
        );

        setInputItems(inputs);
        setOutputItems(outputs);
      } else {
        setErrors({
          ...errors,
          _dataError: "Unable to fetch sub order product data",
        });
      }
    } catch (error) {
      console.error("Error fetching sub order product data:", error);
      setErrors({
        ...errors,
        _dataError: "Error loading data",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [subProductId]);

  const getStatusChip = (status) => {
    let color;
    let bgColor;

    switch (status?.toUpperCase()) {
      case "ACTIVE":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        break;
      case "INACTIVE":
        color = theme.palette.error.main;
        bgColor = alpha(theme.palette.error.main, 0.1);
        break;
      default:
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
    }

    return (
      <Chip
        label={status || "Unknown"}
        size="small"
        sx={{
          bgcolor: bgColor,
          color: color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  const ItemsSection = ({ items, type, icon }) => {
    const importItems = items.filter((i) => i.imports != null);
    const categoryItems = items.filter((i) => i.importCategory != null);

    return (
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            {icon}
            <Typography variant="subtitle1" fontWeight="700" color="primary.main">
              {type} Items
            </Typography>
            <Badge badgeContent={items.length} color="primary" sx={{ ml: 1 }} />
          </Box>

          {/* Import items table */}
          {importItems.length > 0 && (
            <TableContainer
              sx={{
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
                overflow: "hidden",
                mb: categoryItems.length > 0 ? 3 : 0,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Unit Conversion</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Stock Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importItems.map((item, index) => (
                    <TableRow key={item.itemId || index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {item.imports.product_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.imports.itemName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.imports.brand}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color={type === "INPUT" ? "error.main" : "success.main"}
                        >
                          {item.itemQty}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.itemUnit}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.75rem" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          1 {item.itemUnit} = {item.prodUnitsForItemUnit} {item.imports.unitType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={item.imports.qty < 0 ? "error.main" : "success.main"}
                          fontWeight="600"
                        >
                          {item.imports.qty} {item.imports.unitType}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(item.imports.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Categories table */}
          {categoryItems.length > 0 && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1, mt: importItems.length > 0 ? 1 : 0 }}>
                <CategoryIcon sx={{ mr: 0.5, fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary" fontWeight="600">
                  Categories
                </Typography>
              </Box>
              <TableContainer
                sx={{
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryItems.map((item, index) => (
                      <TableRow key={item.itemId || index}>
                        <TableCell>
                          <Typography variant="body2">{item.importCategory.category}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View import items in this category">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                handleViewCategoryItems(
                                  item.importCategory.cat_id,
                                  item.importCategory.category
                                )
                              }
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading sub order product details...
        </Typography>
      </Box>
    );
  }

  if (!subOrderProduct) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Box sx={{ mb: 2 }}>
          <InventoryIcon color="error" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Sub Order Product Not Found
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The requested sub order product data could not be retrieved or does
          not exist.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 2 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <InventoryIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Sub Order Product Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Product Header Information */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Product Information
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Sub Product ID
              </Typography>
              <Typography variant="h6" fontWeight="600">
                {subOrderProduct.subProductId}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Sub Product Name
              </Typography>
              <Typography variant="h6" fontWeight="600" color="primary.main">
                {subOrderProduct.subProductName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {getStatusChip(subOrderProduct.status)}
              </Box>
            </Grid>
          </Grid>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Items
                </Typography>
                <Typography variant="h5" fontWeight="700" color="info.main">
                  {subOrderProduct.subOrderItems.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Input Items
                </Typography>
                <Typography variant="h5" fontWeight="700" color="error.main">
                  {inputItems.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Output Items
                </Typography>
                <Typography variant="h5" fontWeight="700" color="success.main">
                  {outputItems.length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Input Items */}
      {inputItems.length > 0 && (
        <ItemsSection
          items={inputItems}
          type="INPUT"
          icon={<InputIcon sx={{ mr: 1, color: "error.main" }} />}
        />
      )}

      {/* Output Items */}
      {outputItems.length > 0 && (
        <ItemsSection
          items={outputItems}
          type="OUTPUT"
          icon={<OutputIcon sx={{ mr: 1, color: "success.main" }} />}
        />
      )}

      <CategoryItemsViewDialog
        open={categoryViewOpen}
        onClose={() => setCategoryViewOpen(false)}
        categoryName={categoryViewName}
        items={categoryViewItems}
      />

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
        <Button
          variant="contained"
          color={subOrderProduct.status === "ACTIVE" ? "error" : "success"}
          onClick={handleToggleStatus}
        >
          {subOrderProduct.status === "ACTIVE" ? "Deactivate" : "Activate"}
        </Button>
      </Box>
    </Paper>
  );
}

SubOrderProductView.propTypes = {
  subProductId: PropTypes.any,
};

export default SubOrderProductView;
